import { faPeopleGroup } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Grid } from "@mui/material";
import { AppGlobalClassKeys } from "app/appGlobalStyles";
import { MprcButton, MprcContainer, MprcInfiniteLoading, MprcRequestIndicator } from "components";
import { ButtonSave } from "components/buttons";
import { IconRole } from "components/icons/IconRole";
import { MprcContainerViewPort } from "components/MprcContainerViewPort";
import { MprcRequestErrorDialog } from "components/MprcRequestErrorDialog";
import { MprcResolveRequestErrors } from "components/MprcRequestErrorReport";
import { BlockReasonSelectorResponse, CreateUserPayload, FieldValueItem, ManagedUserQueryResponse, RecordInfoDto, UserPhoneTypeSelectorResponse, UserStatus, UserType } from "generated/CoreApi_v1";
import LibUtils from "lib/LibUtils";
import { useSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { NIL } from "uuid";
import { EditUserCredentials } from "views/foundation/user/Home/EditUser/EditUserCredentials";
import { EditUserGroups } from "views/foundation/user/Home/EditUser/EditUserGroups";
import { EditUserRecordFiles } from "views/foundation/user/Home/EditUser/EditUserRecordFiles";
import { EditUserRoles } from "views/foundation/user/Home/EditUser/EditUserRoles";
import { EditUserTopics } from "views/foundation/user/Home/EditUser/EditUserTopics";
import { EditUserFooter } from "./EditUserFooter";
import { EditUserHeader } from "./EditUserHeader";
import { EditUserMainBox } from "./EditUserMainBox";
import { EditUserSecondaryBox } from "./EditUserSecondaryBox";

////////////////////////////////////////////////////////////////////
////////

export type EditUserSubForm = "groups" | "roles" | "topics" | "credentials" | "files";

interface IProps {
  userId?: string;
  reactiving?: boolean;
  onClose: () => void;
  onRecordInfoChange?: (info: RecordInfoDto) => void;
}

export interface RecordInfoUser extends RecordInfoDto {
  lastLoginOn?: Date;
}

interface IBoxValidations {
  mainBox: boolean;
  tabsBox: boolean;
}

////////////////////////////////////////////////////////////////////////////

function getEmptyValidations(): IBoxValidations {
  return { mainBox: false, tabsBox: false };
}

////////////////////////////////////////////////////////////////////////////

export type EditUserContextType = {
  viewErrors: ApiProblemErrors | undefined;
  isEditMode: boolean;
  isNewUser: boolean;
  phoneTypeItems: UserPhoneTypeSelectorResponse[];
  blockReasons: BlockReasonSelectorResponse[];
  readonly: boolean;
  onGetUsername: () => string;
  onFormUpdate: (data: Partial<ManagedUserQueryResponse>) => void;
  onError: (e: any) => void;
  onDialog: (dialog: any) => void;
  onGoToSubForm: (subForm: EditUserSubForm) => void;
};

const EditUserContext = React.createContext<EditUserContextType>(null!);

export const useEditUserContext = () => React.useContext(EditUserContext);

////////////////////////////////////////////////////////////////////////////

/**
 * Edição de Usuários
 * @param props
 */
export const EditUser: React.FC<IProps> = (props) => {
  const { reactiving, onClose } = props;

  const api = useCoreApi();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [userId, setUserId] = useState<string | undefined>(props.userId);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [viewError, setViewError] = useState<any>();
  const [viewData, setViewData] = useState<ManagedUserQueryResponse>(undefined!);
  const [viewFieldsErrors, setViewFieldsErrors] = useState<ApiProblemErrors>();
  const [loadDto, setLoadDto] = useState<ManagedUserQueryResponse>();
  const [recordChanged, setRecordChanged] = useState<boolean>(false);
  const [reloadOnClose, setReloadOnClose] = useState<boolean>(false);
  const [subForm, setSubForm] = useState<any>();
  const [boxValidations, setBoxValidations] = useState<IBoxValidations>(getEmptyValidations());
  const [dialog, setDialog] = useState<any>();

  const [blockReasons, setBlockReasons] = useState<BlockReasonSelectorResponse[]>([]);
  const [phoneTypes, setPhoneTypes] = useState<UserPhoneTypeSelectorResponse[]>([]);

  const readonly = useMemo(() => (viewData?.status == UserStatus.Deleted && reactiving != true) ?? false, [viewData?.status, viewData?.secondaryManagers, reactiving]);
  const canSave = useMemo(() => recordChanged && !readonly && Object.values(boxValidations).filter((x) => x === false).length == 0, [recordChanged, readonly, boxValidations]);
  const isEditMode = useMemo(() => recordChanged || loading || saving, [recordChanged, loading, saving]);
  const isNew = useMemo(() => viewData?.userId == undefined || viewData.userId == NIL, [viewData?.userId]);

  ////////////////////////////////////////////////////////////////////////////

  ///
  const getSaveCommand = (): CreateUserPayload => {
    let allFields = (viewData.customFieldsBasic ?? [])
      .concat(viewData.customFieldsCorporate ?? [])
      .concat(viewData.customFieldsPersonal ?? [])
      .concat(viewData.customFieldsAdditional ?? [])
      .concat(viewData.customFieldsConcealed?.filter((x) => x.fieldValue !== undefined) ?? []);

    let customFields = allFields.map((x) => ({ fieldId: x.fieldId, fieldValue: x.fieldValue } as FieldValueItem));

    let command: CreateUserPayload = {
      name: viewData.name!,
      displayName: viewData.displayName!,
      email: viewData.email ?? null,
      preferredLocale: viewData.preferredLocale ?? null,
      workplaceId: viewData.settings!.isWorkplaceFreeText === false ? viewData.workplace?.workplaceId ?? null : null,
      workplaceText: viewData.settings!.isWorkplaceFreeText === true ? viewData.workplaceText ?? null : null,
      units:
        viewData.unitTypes?.reduce(
          (prevValue, currValue) => (currValue.units ? currValue.units.map((x) => x.key).concat(prevValue) : prevValue),
          [] as string[]
        ) ?? [],
      thumbnailId: viewData.thumbnail?.fileId ?? null,
      userType: viewData.isExternalUser ? UserType.External : UserType.Internal,
      allowUserConnections: viewData.allowUserConnections ?? false,
      receiveDailySummaryEmail: viewData.receiveDailySummaryEmail ?? true,
      managerId: viewData.manager ? viewData.manager.userId : null,
      approverId: viewData.approver ? viewData.approver.userId : null,
      secondaryManagers: viewData.secondaryManagers
        ? viewData.secondaryManagers.filter(p => p.manager != null).map((x) => ({ managerId: x.manager!.userId, managerTypeId: x.managerTypeId }))
        : [],
      admissionDate: viewData.admissionDate ?? null,
      dismissedDate: viewData.dismissedDate ?? null,
      tags: viewData.tags ? viewData.tags.map((x) => x.tagName) : null,
      customFields: customFields,
      blockReasonId: viewData.blockReasonId ?? null,
      unblockAt: viewData.blockReasonId != null && viewData.status == UserStatus.ActiveWithLoginBlocked && viewData.unblockAt ? viewData.unblockAt : null,
      password: viewData.generatePassword === true ? null : viewData.password ?? null,
      generatePassword: viewData.generatePassword ?? false,
      phones: viewData.phones ? viewData.phones.filter((x) => x.phoneNumber) : null,
      socialNetworks: viewData.socialNetworks
        ? viewData.socialNetworks.filter(p => p.socialNetworkId).map((x) => ({ socialNetworkId: x.socialNetworkId, socialNetworkType: x.socialNetworkType }))
        : null,
      accessExpiresOn: viewData.accessExpiresOn ?? null,
      birthDate: viewData.birthDate ?? null,
    };

    return command;
  };

  ///
  const handleSaveChanges = async (goTo?: EditUserSubForm) => {
    if (canSave) {
      var saveSuccess = await postData();
      if (saveSuccess && goTo) handleGoToSubForm(goTo);
    }
    else {
      enqueueSnackbar(t("User.Edit.CompleteFormMessage"), { variant: "error" });
    }
  };

  ///
  const refreshData = (dto: ManagedUserQueryResponse) => {
    setLoadDto(LibUtils.deepCopy(dto));
    setViewData(dto);
    setViewError(undefined);
    setViewFieldsErrors(undefined);
  };

  ///
  const resolveErrors = (e: any | unknown) => {
    var r = MprcResolveRequestErrors(e);
    setViewFieldsErrors(r.fieldsWithError);
    setViewError(r.otherErrors);
    if (r.fieldsWithError != null) enqueueSnackbar(t("Record.SaveFailedWithFieldErrorsMessage", { ns: "shared" }), { variant: "error" });
  };

  ///
  function handleUpdateViewData(newDto: Partial<ManagedUserQueryResponse>) {
    setViewData(state => ({ ...state, ...newDto }));
  }

  ///
  const handleGoToSubForm = (goTo: EditUserSubForm) => {
    switch (goTo) {
      case "roles":
        setSubForm(
          <EditUserRoles
            userId={viewData.userId!}
            userName={viewData.name!}
            userType={viewData.userType!}
            viewMode="form"
            onClose={() => setSubForm(null)}
          />
        );
        break;
      case "groups":
        setSubForm(
          <EditUserGroups
            userId={viewData.userId!}
            userName={viewData.name!}
            viewMode="form"
            onClose={() => setSubForm(null)}
          />
        );
        break;
      case "topics":
        setSubForm(
          <EditUserTopics
            userId={viewData.userId}
            userName={viewData.name}
            editMode="default"
            onClose={() => setSubForm(null)}
          />
        );
        break;
      case "credentials":
        setSubForm(
          <EditUserCredentials
            userId={viewData.userId}
            userName={viewData.name}
            editMode="default"
            onClose={() => setSubForm(null)}
          />
        );
        break;
      case "files":
        setSubForm(
          <EditUserRecordFiles
            userId={viewData.userId}
            userName={viewData.name}
            editMode="default"
            onClose={() => setSubForm(null)}
            canDeleteFiles={!readonly}
            canAddFiles={!readonly}
          />
        );
        break;
    }
  };

  ///
  const getHeaderItems = () => {
    const formHeaderOptions: React.ReactNode[] = [];

    if (!isNew) {
      formHeaderOptions.push(
        <MprcButton
          key="btnGroup"
          rounded
          variant="text"
          size="large"
          color="info"
          startIcon={<FontAwesomeIcon icon={faPeopleGroup} />}
          onClick={() => handleGoToSubForm("groups")}
          disabled={isEditMode}
        >
          {t("SysObjects.Groups", { ns: "shared" })}
        </MprcButton>
      );

      if (viewData?.settings.canAssignRoles == true) {
        formHeaderOptions.push(
          <MprcButton
            key="btnRole"
            rounded
            variant="text"
            size="large"
            color="info"
            startIcon={<IconRole />}
            onClick={() => handleGoToSubForm("roles")}
            disabled={isEditMode}
          >
            {t("SysObjects.RolesAndPermissions", { ns: "shared" })}
          </MprcButton>
        );
      }
    }

    formHeaderOptions.push(<ButtonSave key="btnSave" rounded size="large" variant="text" onClick={() => handleSaveChanges()} disabled={!canSave} />);

    return formHeaderOptions;
  };

  ////////////////////////////////////////////////////////////////////////////
  /// RENDER
  ////////////////////////////////////////////////////////////////////////////

  const Header = (
    <EditUserHeader reloadOnClose={reloadOnClose} recordChanged={recordChanged} onClose={onClose} actions={getHeaderItems()} />
  );

  const MainForm = () => (
    <Grid container direction="column" height="100%">
      <Grid width="100%" overflow="auto" className={AppGlobalClassKeys.thinScrollbar}>
        <MprcContainerViewPort>
          <MprcContainer>
            <EditUserMainBox
              initialBlockProps={loadDto == null ? null : { blockedOn: loadDto.blockedOn, blockReasonId: loadDto.blockReasonId, blocker: loadDto.blocker }}
              loadDto={loadDto ?? null}
              viewData={{
                userId: viewData.userId,
                name: viewData.name,
                displayName: viewData.displayName,
                email: viewData.email,
                preferredLocale: viewData.preferredLocale,
                status: viewData.status,
                workplace: viewData.workplace,
                workplaceText: viewData.workplaceText,
                thumbnail: viewData.thumbnail,
                isExternalUser: viewData.isExternalUser,
                accessExpiresOn: viewData.accessExpiresOn,
                blockReasonId: viewData.blockReasonId,
                unblockAt: viewData.unblockAt,
                blocker: viewData.blocker,
                blockedOn: viewData.blockedOn,
                settings: viewData.settings,
                hasSamlAccount: viewData.hasSamlAccount,
                generatePassword: viewData.generatePassword,
                password: viewData.password,
                userType: viewData.userType,
                customFieldsBasic: viewData.customFieldsBasic
              }}
              onFormValidation={(valid) =>
                setBoxValidations((state) => {
                  return { ...state, mainBox: valid };
                })
              }
            />

            <EditUserSecondaryBox
              viewData={{
                admissionDate: viewData.admissionDate,
                approver: viewData.approver,
                customFieldsCorporate: viewData.customFieldsCorporate,
                dismissedDate: viewData.dismissedDate,
                manager: viewData.manager,
                secondaryManagers: viewData.secondaryManagers,
                unitTypes: viewData.unitTypes,
                birthDate: viewData.birthDate,
                customFieldsPersonal: viewData.customFieldsPersonal,
                phones: viewData.phones,
                socialNetworks: viewData.socialNetworks,
                customFieldsAdditional: viewData.customFieldsAdditional,
                customFieldsConcealed: viewData.customFieldsConcealed,
                tags: viewData.tags,
                userId: viewData.userId
              }}
              onFormValidation={(valid) =>
                setBoxValidations((state) => {
                  return { ...state, tabsBox: valid };
                })
              }
            />
            {viewData.userId && viewData.recordInfo && (
              <EditUserFooter
                userId={viewData.userId}
                name={viewData.name!}
                recordInfo={{ ...viewData.recordInfo, lastLoginOn: viewData.lastLoginOn } as RecordInfoUser}
              />
            )}
          </MprcContainer>
        </MprcContainerViewPort>
      </Grid>
    </Grid>
  );

  function loadPhoneTypes() {
    api.v1.selectors.userPhoneTypes()
      .then((response) => {
        setPhoneTypes(response.data);
      });
  }

  function loadBlockReasons() {
    api.v1.selectors.loginBlockReasons()
      .then((response) => {
        setBlockReasons(response.data);
      });
  }


  // Ao carregar um DTO:
  useEffect(() => {
    async function getData() {

      if (phoneTypes.length == 0) loadPhoneTypes();
      if (blockReasons.length == 0) loadBlockReasons();

      try {
        var response = await api.v1.userManagement.getUser({ userId: userId ?? null });
        let data = response.data;
        if (userId === undefined) {
          data = LibUtils.convertNullToUndefined(data);
        }

        refreshData(data);

        if (reactiving) {
          setViewData({ ...data, displayName: data.displayName.replace(" *", "") });
        }
      }
      catch (e: any) {
        setViewError(e as Error);
      }
      finally {
        setLoading(false);
      }
    }

    if (loading) getData();
  }, [loading]);

  async function postData(): Promise<boolean> {
    let success = false;
    setSaving(true);
    try {
      let result;
      if (userId === undefined) {
        result = await api.v1.userManagement.createUser(getSaveCommand() as CreateUserPayload);
        setUserId(result.data);
      } else {
        result = await api.v1.userManagement.updateUser({ ...getSaveCommand(), reactiving: reactiving ?? false, userId: viewData.userId! });
      }
      setLoading(true);
      setReloadOnClose(true);
      enqueueSnackbar(t("Record.SaveSuccessMessage", { ns: "shared" }), { variant: "success" });
      success = true;
    } catch (e) {
      resolveErrors(e);
      success = false;
    }
    finally {
      setSaving(false);
    }

    return success;
  }

  // Ao editar o DTO atual:
  useEffect(() => {
    const isRecordChanged = !LibUtils.deepCompare(viewData, loadDto);
    setRecordChanged(isRecordChanged);
  }, [viewData]);

  useEffect(() => {
    if (reactiving == true) {
      enqueueSnackbar(t("User.Edit.ReactivateMessage"), { variant: "warning" });
    }
  }, []);

  return (
    <Box height="100%">
      <MprcRequestIndicator visible={saving} type={"post"} />
      <MprcRequestErrorDialog error={viewError} onClose={() => {
        setViewError(undefined);
        onClose();
      }} />

      {subForm}
      {dialog}
      <EditUserContext.Provider
        value={
          useMemo(() => ({
            isNewUser: isNew,
            isEditMode: isEditMode,
            readonly,
            phoneTypeItems: phoneTypes,
            blockReasons: blockReasons,
            viewErrors: viewFieldsErrors,
            onFormUpdate: handleUpdateViewData,
            onError: e => setViewError(e),
            onDialog: (dialog) => setDialog(dialog),
            onGoToSubForm: handleGoToSubForm,
            onGetUsername: () => viewData.name,
          }), [isNew, isEditMode, readonly, phoneTypes, blockReasons, viewFieldsErrors])
        }
      >
        <Grid container direction="column" width="100%" height="100%" sx={{ display: subForm ? "none" : "" }}>
          <Grid width="100%">
            {Header}
          </Grid>
          <Grid size="grow" width="100%" flexGrow={1} overflow="hidden">
            <MprcInfiniteLoading visible={loading} />
            {!loading && viewData && MainForm()}
          </Grid>
        </Grid>
      </EditUserContext.Provider>
    </Box>
  );
};
